import { useContext, useEffect } from "react";
import { Navigate } from "react-router";
import { AuthContext, AuthenticationContext } from "../AuthProvider";
import { useGetConfig } from "../../hooks/useGetConfig";
import {
  AddressAndAccountContext,
  AddressAndAccountContextType,
} from "../AddressAndAccountProvider";
import { useNavigate, useLocation } from "react-router-dom";
import BillingProvider from "../BillingProvider";

const ProtectedRoute = ({
  redirectPath = "/",
  children,
}: {
  redirectPath?: string;
  children: React.ReactElement;
}) => {
  const { isAuthenticated } = useContext(AuthContext) as AuthenticationContext;
  const { userInfo } = useContext(
    AddressAndAccountContext
  ) as AddressAndAccountContextType;
  const navigate = useNavigate();
  const location = useLocation();

  const { data: config } = useGetConfig();
  const showBilling = config?.data?.config?.enabled_features?.BILLING?.enabled;

  const formerLocation = {
    formerLocation: window.location.pathname,
  };

  useEffect(() => {
    // Skip check if already on security questions page
    if (location.pathname === "/security-questions") {
      return;
    }

    // Check localStorage first for faster response
    const securityQuestionsAnswered = localStorage.getItem(
      "securityQuestionsAnswered"
    );

    // If they haven't answered security questions (we know from localStorage), redirect immediately
    if (securityQuestionsAnswered === "false") {
      navigate("/security-questions", { replace: true });
      return;
    }

    // If we know they've answered security questions from localStorage, no need to check user data
    if (securityQuestionsAnswered === "true") {
      return;
    }

    // Only check user data if we don't have info in localStorage
    const user = userInfo?.data?.user;
    if (user) {
      // Store the result in localStorage for future checks
      localStorage.setItem(
        "securityQuestionsAnswered",
        user.answered_security_questions ? "true" : "false"
      );

      // If security questions not answered, redirect
      if (!user.answered_security_questions) {
        navigate("/security-questions", { replace: true });
      }
    }
  }, [userInfo, navigate, location.pathname]);

  if (isAuthenticated === null) {
    return null;
  }

  if (formerLocation?.formerLocation === "/billing" && !showBilling) {
    return <Navigate to={"/"} />;
  }

  if (isAuthenticated === false) {
    return <Navigate to={redirectPath} state={formerLocation} />;
  }

  return <BillingProvider>{children}</BillingProvider>;
};
export default ProtectedRoute;
